import type { Coordinate } from "../../entities/tracking";

/**
 * Distance d'un point à un tracé — module PUR, testable sans mock.
 *
 * Sert à répondre à une seule question, mais elle est centrale au suivi :
 * **la Main est-elle encore sur l'itinéraire qu'on lui dessine ?**
 *
 * Tant qu'elle l'est, le tracé reste valable et il n'y a rien à recalculer.
 * Dès qu'elle s'en écarte — mauvaise rue, détour, itinéraire calculé depuis un
 * point qu'elle a quitté — la ligne cesse de décrire son trajet, et c'est le
 * moment de redemander une route à Google. C'est ce qui évite les deux
 * extrêmes : recalculer à chaque point GPS (coûteux et inutile), ou ne jamais
 * recalculer (le tracé finit par n'avoir plus aucun rapport avec la réalité).
 */

/**
 * Mètres par degré de latitude. Constante à toutes latitudes.
 * (Un degré de longitude, lui, rétrécit avec le cosinus de la latitude.)
 */
const M_PER_DEG_LAT = 111_320;

/**
 * Distance point-segment en mètres, en projection équirectangulaire locale.
 *
 * Sur quelques kilomètres et près de l'équateur — Cotonou est à 6° N —
 * l'approximation plane est exacte à mieux que le mètre, très en dessous de la
 * précision d'un GPS de téléphone. Faire du haversine par segment coûterait
 * plus cher pour un résultat identique à l'usage.
 */
function distanceToSegmentM(p: Coordinate, a: Coordinate, b: Coordinate): number {
  const latRad = (p.latitude * Math.PI) / 180;
  const mPerDegLng = M_PER_DEG_LAT * Math.cos(latRad);

  // Repère métrique local centré sur `a`.
  const px = (p.longitude - a.longitude) * mPerDegLng;
  const py = (p.latitude - a.latitude) * M_PER_DEG_LAT;
  const bx = (b.longitude - a.longitude) * mPerDegLng;
  const by = (b.latitude - a.latitude) * M_PER_DEG_LAT;

  const lenSq = bx * bx + by * by;
  // Segment dégénéré (deux points confondus) : c'est une distance point-point.
  if (lenSq === 0) return Math.hypot(px, py);

  // Projection de p sur [a,b], bornée au segment.
  const t = Math.max(0, Math.min(1, (px * bx + py * by) / lenSq));
  return Math.hypot(px - t * bx, py - t * by);
}

/**
 * Distance minimale, en mètres, entre un point et un tracé polyligne.
 *
 * Rend `null` si le tracé est vide : « aucune distance » n'est pas « distance
 * nulle », et confondre les deux ferait croire la Main parfaitement sur sa
 * route alors qu'aucune route n'existe.
 */
export function distanceToPolylineM(
  point: Coordinate | null | undefined,
  polyline: Coordinate[] | null | undefined,
): number | null {
  if (!point || !polyline || polyline.length === 0) return null;
  if (polyline.length === 1) return distanceToSegmentM(point, polyline[0], polyline[0]);

  let min = Infinity;
  for (let i = 1; i < polyline.length; i += 1) {
    const d = distanceToSegmentM(point, polyline[i - 1], polyline[i]);
    if (d < min) min = d;
  }
  return min;
}

/**
 * Écart au-delà duquel on considère la Main SORTIE de son itinéraire.
 *
 * 150 m : assez large pour absorber le bruit GPS urbain et la largeur d'un
 * carrefour, assez serré pour détecter un vrai changement de rue. En dessous,
 * on redemanderait une route à chaque imprécision ; au-dessus, la Main pourrait
 * suivre une parallèle entière sans qu'on s'en aperçoive.
 */
export const OFF_ROUTE_THRESHOLD_M = 150;

/** La Main s'est-elle écartée du tracé au point qu'il faille en redemander un ? */
export function isOffRoute(
  point: Coordinate | null | undefined,
  polyline: Coordinate[] | null | undefined,
  thresholdM: number = OFF_ROUTE_THRESHOLD_M,
): boolean {
  const d = distanceToPolylineM(point, polyline);
  // Sans tracé, il n'y a rien dont s'écarter — mais il y a tout à calculer.
  if (d == null) return true;
  return d > thresholdM;
}

// ────────────────────────────────────────────────────────────────────────────
// SUIVRE le tracé, et non plus seulement s'en écarter
// ────────────────────────────────────────────────────────────────────────────
//
// Tout ce qui précède répond à « la Main est-elle encore sur sa route ? ».
// Ce qui suit répond à la question d'après, qui est celle que le client se pose
// en regardant sa carte : « OÙ est-elle SUR cette route, et par où passe le
// reste du trajet ? »
//
// C'est la différence entre un marqueur qui saute d'un point GPS au suivant en
// coupant les pâtés de maisons, et une moto qui glisse le long de la rue, prend
// les virages et laisse derrière elle le chemin déjà parcouru.
//
// Deux primitives suffisent :
//   • PROJETER une position sur le tracé → une abscisse curviligne, un nombre
//     de mètres depuis le départ. C'est ce qui remplace « lat/lng » comme
//     coordonnée de travail : le long d'une route, avancer c'est augmenter ce
//     nombre.
//   • REVENIR d'une abscisse à un point (et à un cap). C'est ce qui permet
//     d'animer entre deux abscisses en restant sur le bitume.

/** Cap entre deux points, en degrés depuis le nord (sens horaire). */
export function bearingBetween(from: Coordinate, to: Coordinate): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLon = toRad(to.longitude - from.longitude);
  const lat1 = toRad(from.latitude);
  const lat2 = toRad(to.latitude);

  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

  return (Math.atan2(y, x) * 180) / Math.PI;
}

/** Distance entre deux points, en mètres (même approximation locale plane). */
export function metersBetween(a: Coordinate, b: Coordinate): number {
  const latRad = (((a.latitude + b.latitude) / 2) * Math.PI) / 180;
  const mPerDegLng = M_PER_DEG_LAT * Math.cos(latRad);
  const dx = (b.longitude - a.longitude) * mPerDegLng;
  const dy = (b.latitude - a.latitude) * M_PER_DEG_LAT;
  return Math.hypot(dx, dy);
}

/**
 * Tracé MESURÉ : ses points, plus la distance cumulée jusqu'à chacun.
 *
 * ## Pourquoi pré-calculer
 * L'animation interroge le tracé à CHAQUE FRAME — soixante fois par seconde.
 * Un itinéraire urbain en qualité fine compte plusieurs centaines de points ;
 * re-sommer ses segments à chaque frame reviendrait à refaire le même calcul
 * des dizaines de milliers de fois par seconde pour un résultat invariable.
 *
 * Mesuré une fois, le tracé se lit ensuite par recherche dichotomique : le coût
 * d'une frame ne dépend plus de la longueur de l'itinéraire.
 */
export type MeasuredPolyline = {
  points: Coordinate[];
  /** `cumulativeM[i]` = distance du point 0 au point i. Même longueur que `points`. */
  cumulativeM: number[];
  /** Longueur totale du tracé, en mètres. */
  lengthM: number;
};

export function measurePolyline(points: Coordinate[]): MeasuredPolyline {
  const cumulativeM: number[] = new Array(points.length);
  let total = 0;
  for (let i = 0; i < points.length; i += 1) {
    if (i > 0) total += metersBetween(points[i - 1], points[i]);
    cumulativeM[i] = total;
  }
  return { points, cumulativeM, lengthM: total };
}

/** Position d'un point rapportée au tracé. */
export type PolylineProjection = {
  /** Segment `[i, i+1]` le plus proche. */
  segmentIndex: number;
  /** Le point du tracé le plus proche — la position « recalée sur le bitume ». */
  point: Coordinate;
  /** Écart perpendiculaire au tracé, en mètres. */
  offsetM: number;
  /** Abscisse curviligne du point projeté, en mètres depuis le départ. */
  alongM: number;
};

/**
 * Projette une position sur le tracé.
 *
 * Appelé une fois par position GPS reçue — pas par frame : c'est la seule
 * opération de ce module qui parcourt tout le tracé.
 *
 * Rend `null` si le tracé n'a pas au moins deux points : on ne projette pas sur
 * un chemin qui n'existe pas.
 */
export function projectOntoPolyline(
  point: Coordinate | null | undefined,
  measured: MeasuredPolyline | null | undefined,
): PolylineProjection | null {
  if (!point || !measured || measured.points.length < 2) return null;

  const { points, cumulativeM } = measured;
  let best: PolylineProjection | null = null;

  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];

    const latRad = (point.latitude * Math.PI) / 180;
    const mPerDegLng = M_PER_DEG_LAT * Math.cos(latRad);

    const px = (point.longitude - a.longitude) * mPerDegLng;
    const py = (point.latitude - a.latitude) * M_PER_DEG_LAT;
    const bx = (b.longitude - a.longitude) * mPerDegLng;
    const by = (b.latitude - a.latitude) * M_PER_DEG_LAT;

    const lenSq = bx * bx + by * by;
    const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, (px * bx + py * by) / lenSq));
    const offsetM = Math.hypot(px - t * bx, py - t * by);

    if (best && offsetM >= best.offsetM) continue;

    const segLenM = cumulativeM[i] - cumulativeM[i - 1];
    best = {
      segmentIndex: i - 1,
      point: {
        latitude: a.latitude + (b.latitude - a.latitude) * t,
        longitude: a.longitude + (b.longitude - a.longitude) * t,
      },
      offsetM,
      alongM: cumulativeM[i - 1] + t * segLenM,
    };
  }

  return best;
}

/**
 * Index du dernier point dont l'abscisse est ≤ `alongM`.
 *
 * Dichotomie : c'est ce qui rend le coût d'une frame indépendant de la longueur
 * du tracé.
 */
function segmentIndexAt(measured: MeasuredPolyline, alongM: number): number {
  const { cumulativeM } = measured;
  let lo = 0;
  let hi = cumulativeM.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (cumulativeM[mid] <= alongM) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * Le point situé à `alongM` mètres le long du tracé.
 *
 * C'est la fonction qu'appelle l'animation : faire avancer un marqueur revient
 * à faire croître `alongM`, et à demander ici où cela se trouve. Le résultat est
 * toujours SUR le tracé — c'est la raison pour laquelle la moto suit les rues
 * au lieu de les couper.
 *
 * L'abscisse est bornée aux extrémités : avant le départ, on rend le départ ;
 * au-delà de l'arrivée, l'arrivée.
 */
export function pointAtDistanceM(
  measured: MeasuredPolyline | null | undefined,
  alongM: number,
): Coordinate | null {
  if (!measured || measured.points.length === 0) return null;
  const { points, cumulativeM } = measured;
  if (points.length === 1) return points[0];

  if (alongM <= 0) return points[0];
  if (alongM >= measured.lengthM) return points[points.length - 1];

  const i = segmentIndexAt(measured, alongM);
  if (i >= points.length - 1) return points[points.length - 1];

  const a = points[i];
  const b = points[i + 1];
  const segLenM = cumulativeM[i + 1] - cumulativeM[i];
  const t = segLenM === 0 ? 0 : (alongM - cumulativeM[i]) / segLenM;

  return {
    latitude: a.latitude + (b.latitude - a.latitude) * t,
    longitude: a.longitude + (b.longitude - a.longitude) * t,
  };
}

/**
 * Cap TANGENT au tracé à l'abscisse `alongM`.
 *
 * Meilleur que le cap déduit de deux positions GPS successives : celui-là
 * hésitait avec le bruit du capteur et pointait « à travers » les virages,
 * puisqu'il joignait deux points sans rien savoir de la rue entre les deux.
 * Ici l'icône s'aligne sur le bitume — elle tourne dans le virage, avec lui.
 *
 * `null` sur un tracé dégénéré (moins de deux points distincts).
 */
export function headingAtDistanceM(
  measured: MeasuredPolyline | null | undefined,
  alongM: number,
): number | null {
  if (!measured || measured.points.length < 2) return null;
  const { points, cumulativeM } = measured;

  const i = Math.min(
    Math.max(segmentIndexAt(measured, Math.max(0, Math.min(alongM, measured.lengthM))), 0),
    points.length - 2,
  );

  // Segment de longueur nulle (points dupliqués par le fournisseur) : on
  // cherche le premier segment voisin qui porte réellement une direction.
  for (let j = i; j < points.length - 1; j += 1) {
    if (cumulativeM[j + 1] > cumulativeM[j]) return bearingBetween(points[j], points[j + 1]);
  }
  for (let j = i; j > 0; j -= 1) {
    if (cumulativeM[j] > cumulativeM[j - 1]) return bearingBetween(points[j - 1], points[j]);
  }
  return null;
}

/**
 * Le tracé RESTANT à partir de `alongM` — ce qu'il y a encore à parcourir.
 *
 * Le premier point rendu est exactement la position à `alongM` : le trait part
 * donc du marqueur, sans décrochement visible.
 */
export function sliceFromDistanceM(
  measured: MeasuredPolyline | null | undefined,
  alongM: number,
): Coordinate[] {
  if (!measured || measured.points.length === 0) return [];
  const { points } = measured;
  if (points.length === 1) return [...points];
  if (alongM <= 0) return [...points];
  if (alongM >= measured.lengthM) return [points[points.length - 1]];

  const head = pointAtDistanceM(measured, alongM);
  const i = segmentIndexAt(measured, alongM);
  const rest = points.slice(i + 1);
  return head ? [head, ...rest] : rest;
}

/**
 * Le tracé DÉJÀ PARCOURU, du départ jusqu'à `alongM`.
 *
 * Dessiné en retrait derrière la Main, il donne au client ce qu'aucune ligne
 * unique ne dit : la part du chemin qui est faite.
 */
export function sliceToDistanceM(
  measured: MeasuredPolyline | null | undefined,
  alongM: number,
): Coordinate[] {
  if (!measured || measured.points.length === 0) return [];
  const { points } = measured;
  if (alongM <= 0) return [points[0]];
  if (alongM >= measured.lengthM) return [...points];

  const tail = pointAtDistanceM(measured, alongM);
  const i = segmentIndexAt(measured, alongM);
  const head = points.slice(0, i + 1);
  return tail ? [...head, tail] : head;
}
